'use client';
import { IconButton, ItemShape, Text } from '@stone-payments/jade';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { ApplePayLogoIllustration } from '../../applePay/components/applePayLogo.component';
import { ClickToPayLogoIllustration } from '../../clickToPay/components/clickToPayLogo.illustration';
import { GooglePayLogoIllustration } from '../../googlePay/components/googlePayLogo.component';
import type { SelectedPaymentMethodType } from '../../hooks/useSelectedPaymentMethod';

type SelectedPaymentMethodProps = {
  method: SelectedPaymentMethodType;
  onEdit: () => void;
  disabled?: boolean;
};

const WalletLogo = ({
  children,
  widthClassName = 'w-jade-600',
  keepOriginalSize = false,
}: {
  children: ReactNode;
  widthClassName?: string;
  keepOriginalSize?: boolean;
}) => (
  <div
    className={`flex shrink-0 items-center justify-center ${
      keepOriginalSize ? '' : `${widthClassName} [&_svg]:h-auto [&_svg]:w-full`
    }`}
  >
    {children}
  </div>
);

const methods = {
  credit_card: {
    labelId: 'paymentMethod.credit_card',
    icon: <ItemShape size="small" use="card" variant="neutral" />,
  },
  pix: {
    labelId: 'paymentMethod.pix',
    icon: <ItemShape size="small" use="brand-pix" variant="neutral" />,
  },
  boleto: {
    labelId: 'paymentMethod.boleto',
    icon: <ItemShape size="small" use="barcode" variant="neutral" />,
  },
  applePay: {
    labelId: 'paymentMethod.applePay',
    icon: (
      <WalletLogo widthClassName="w-jade-500">
        <ApplePayLogoIllustration />
      </WalletLogo>
    ),
  },
  googlePay: {
    labelId: 'paymentMethod.googlePay',
    icon: (
      <WalletLogo>
        <GooglePayLogoIllustration />
      </WalletLogo>
    ),
  },
  clickToPay: {
    labelId: 'paymentMethod.clickToPay',
    icon: (
      <WalletLogo keepOriginalSize>
        <ClickToPayLogoIllustration />
      </WalletLogo>
    ),
  },
};

export function SelectedPaymentMethod({
  method,
  onEdit,
  disabled = false,
}: SelectedPaymentMethodProps) {
  const { formatMessage } = useIntl();
  const { labelId, icon } = methods[method];

  return (
    <div
      className="flex flex-col gap-jade-300"
      data-testid="selected-payment-method"
    >
      <Text variant="heading-small" color="high">
        {formatMessage({ id: 'paymentMethod.selected.title' })}
      </Text>
      <div className="flex items-center gap-jade-200 rounded-jade-medium border border-solid border-jade-border-low bg-jade-background-surface px-jade-200 py-jade-100 shadow-sm">
        {icon}
        <Text
          variant="text-large"
          weight="semibold"
          color="high"
          className="flex-1"
        >
          {formatMessage({ id: labelId })}
        </Text>
        <IconButton
          size="small"
          variant="neutral-subtle"
          icon="action-edit"
          aria-label={formatMessage({ id: 'paymentMethod.selected.edit' })}
          disabled={disabled}
          onClick={onEdit}
        />
      </div>
    </div>
  );
}
